import { useCallback, useEffect, useState } from 'react';
import * as briefsManager from '../services/briefs';
import { BriefUnsubscribeLink } from '../services/models/brief-unsubscribe-link';
import { useServiceErrorHandler } from './useServiceErrorHandler';

// Local state machine for the public brief-unsubscribe pages; mirrors the
// retired briefUnsubscribeLink Redux slice (fetch link -> confirm -> done).
export const useBriefUnsubscribe = (memberId: string | null, code: string | null) => {
  const handleServiceError = useServiceErrorHandler();

  const [briefUnsubscribeLink, setBriefUnsubscribeLink] = useState<BriefUnsubscribeLink>({});

  useEffect(() => {
    if (!memberId || !code) {
      return;
    }

    let cancelled = false;
    setBriefUnsubscribeLink(current => ({ ...current, isFetching: true }));
    briefsManager
      .getBriefUnsubscribeLink(memberId, code)
      .then(link => {
        if (!cancelled) setBriefUnsubscribeLink(current => ({ ...current, ...link }));
      })
      .catch(error => handleServiceError(error))
      .finally(() => {
        if (!cancelled) setBriefUnsubscribeLink(current => ({ ...current, isFetching: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [memberId, code, handleServiceError]);

  const unsubscribe = useCallback(async () => {
    if (!memberId || !code) {
      return;
    }

    setBriefUnsubscribeLink(current => ({ ...current, isUnsubscribing: true }));
    try {
      await briefsManager.unsubscribeBriefRecipient(memberId, code);
      setBriefUnsubscribeLink(current => ({
        ...current,
        isUnsubscribing: false,
        isCompleted: true,
      }));
    } catch (error) {
      handleServiceError(error, () =>
        setBriefUnsubscribeLink(current => ({ ...current, isUnsubscribing: false }))
      );
    }
  }, [memberId, code, handleServiceError]);

  return { briefUnsubscribeLink, unsubscribe };
};
